import _extends from "@babel/runtime/helpers/extends";
import React from "react";
import SyntaxHighlighter from "react-syntax-highlighter";
import { stackoverflowDark, stackoverflowLight } from "react-syntax-highlighter/dist/esm/styles/hljs";
import ReactMarkdown from "react-markdown";
import styled, { useTheme } from "styled-components";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Alert, Badge, Box, Link, Text } from "~/components-shared";
import moment from "moment";
import Collapsable from "~/components/Collapsable";
import { CollapsableTypeEnum } from "~/types/enums";
const H1 = styled.h1`
  font-size: 2rem;
  font-weight: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.typography.fontWeight.semiBold;
}};
  line-height: 1.4em;
  margin-top: 1em;
  margin-bottom: 0.2em;

  &:first-child {
    margin-top: 0;
  }
`;
const H2 = styled.h2`
  font-size: 1.6rem;
  font-weight: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.typography.fontWeight.semiBold;
}};
  line-height: 1.6em;
  margin-top: 0.8em;
  margin-bottom: 0.2em;

  &:first-child {
    margin-top: 0;
  }
`;
const H3 = styled.h3`
  font-size: 1.4rem;
  font-weight: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.typography.fontWeight.semiBold;
}};
  margin-top: 0.6em;
  margin-bottom: 0.2em;

  &:first-child {
    margin-top: 0;
  }
`;
const H4 = styled.h4`
  font-size: 1.2rem;
  font-weight: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return theme.typography.fontWeight.semiBold;
}};
  margin-top: 0.4em;
  margin-bottom: 0.2em;

  &:first-child {
    margin-top: 0;
  }
`;
const H5 = styled.h5`
  font-size: 1rem;
  font-weight: ${_ref5 => {
  let {
    theme
  } = _ref5;
  return theme.typography.fontWeight.semiBold;
}};
  margin-top: 0.2em;
  margin-bottom: 0.2em;

  &:first-child {
    margin-top: 0;
  }
`;
const H6 = styled.h6`
  font-size: 0.8rem;
  font-weight: ${_ref6 => {
  let {
    theme
  } = _ref6;
  return theme.typography.fontWeight.semiBold;
}};
  margin-top: 0.1em;
  margin-bottom: 0.2em;

  &:first-child {
    margin-top: 0;
  }
`;
const FormattedCode = styled(SyntaxHighlighter)`
  border-radius: ${_ref7 => {
  let {
    theme
  } = _ref7;
  return theme.shape.borderRadius.primary;
}};
  background-color: rgba(0, 0, 0, 0.1) !important;
  margin-bottom: 1em;

  & > code > span,
  & > code {
    font-size: 0.9rem;
    line-height: 1.2em;
    white-space: pre-wrap;
    font-family: ${_ref8 => {
  let {
    theme
  } = _ref8;
  return theme.typography.fontFamily.code;
}};
  }
`;
const InlineCode = styled.code`
  font-size: 0.9em;
  padding: 2px 5px;
  color: ${_ref9 => {
  let {
    theme
  } = _ref9;
  return theme.colors.primary.main;
}};
  border-radius: ${_ref10 => {
  let {
    theme
  } = _ref10;
  return theme.shape.borderRadius.primary;
}};
  font-family: ${_ref11 => {
  let {
    theme
  } = _ref11;
  return theme.typography.fontFamily.code;
}};
  background-color: rgba(0, 0, 0, 0.1);
`;
const UnorderedList = styled.ul`
  padding-left: 1.5em;

  &:not(:last-child) {
    margin-bottom: 1em;
  }
`;
const OrderedList = styled.ol`
  padding-left: 1.5em;

  &:not(:last-child) {
    margin-bottom: 1em;
  }
`;
const ListItem = styled.li``;
const Span = styled.span`
  white-space: break-spaces;
`;
const Quote = styled.blockquote`
  position: relative;
  padding-left: 0.7em;
  display: block;

  &:before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    border-radius: 3px;
    background-color: ${_ref12 => {
  let {
    theme
  } = _ref12;
  return theme.colors.primary.main;
}};
    width: 3px;
    height: 100%;
  }

  &:not(:last-child) {
    margin-bottom: 1em;
  }
`;
const Paragraph = styled.span`
  display: block;
  white-space: break-spaces;

  &:not(:last-child) {
    margin-bottom: 0.7em;
  }
`;
const TableWrapper = styled.div`
  overflow: auto;
  width: 100%;
  max-height: 500px;
  -webkit-overflow-scrolling: touch;
  border-radius: 6px;
  border: 1px solid ${_ref13 => {
  let {
    theme
  } = _ref13;
  return theme.colors.borders.primary;
}};
  font-size: ${_ref14 => {
  let {
    theme
  } = _ref14;
  return `${theme.typography.fontStep * 3.5}rem`;
}};
  background-color: ${_ref15 => {
  let {
    theme
  } = _ref15;
  return theme.colors.background.primary;
}};

  &:not(:last-child) {
    margin-bottom: 1em;
  }
`;
const Table = styled.table`
  width: 100%;
`;
const Th = styled.th`
  padding: 0.5em;
  white-space: nowrap;
  text-align: left;
  font-weight: ${_ref16 => {
  let {
    theme
  } = _ref16;
  return theme.typography.fontWeight.semiBold;
}};
  background-color: ${_ref17 => {
  let {
    theme
  } = _ref17;
  return theme.colors.background.primary;
}};
  position: sticky;
  z-index: 2;
  top: 0;

  &:not(:last-child) {
    border-right: 1px solid ${_ref18 => {
  let {
    theme
  } = _ref18;
  return theme.colors.borders.primary;
}};
  }

  &:before {
    content: "";
    position: absolute;
    background-color: ${_ref19 => {
  let {
    theme
  } = _ref19;
  return theme.colors.borders.primary;
}};
    bottom: 2px;
    width: 100%;
    height: 1px;
    left: 0;
  }
`;
const Td = styled.td`
  padding: 0.5em;
  min-height: 1em;
  text-align: left;
  height: 2.8em;
  vertical-align: top;

  &:not(:last-child) {
    border-right: 1px solid ${_ref20 => {
  let {
    theme
  } = _ref20;
  return theme.colors.borders.primary;
}};
  }
`;
const Tr = styled.tr`
  &:not(:last-child) {
    border-bottom: 1px solid ${_ref21 => {
  let {
    theme
  } = _ref21;
  return theme.colors.borders.primary;
}};
  }
`;
const Ins = styled.ins`
  text-decoration: none;
  position: relative;
  z-index: 1;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${_ref22 => {
  let {
    theme
  } = _ref22;
  return theme.colors.success.secondary;
}};
    opacity: 0.5;
    border-radius: 3px;
    z-index: -1;
  }
`;
const Del = styled.del`
  text-decoration: line-through;
  position: relative;
  z-index: 1;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${_ref23 => {
  let {
    theme
  } = _ref23;
  return theme.colors.danger.secondary;
}};
    opacity: 0.5;
    border-radius: 3px;
    z-index: -1;
  }
`;
const Underline = styled.u`
  text-decoration: underline;
`;
const defaultComponents = (linkColor, fontSize) => ({
  "joyn-badge": _ref24 => {
    let {
      children,
      color
    } = _ref24;
    return /*#__PURE__*/React.createElement(Badge, {
      fontSize: fontSize,
      color: color
    }, children);
  },
  "joyn-del": _ref25 => {
    let {
      children
    } = _ref25;
    return /*#__PURE__*/React.createElement(Del, null, children);
  },
  "joyn-u": _ref26 => {
    let {
      children
    } = _ref26;
    return /*#__PURE__*/React.createElement(Underline, null, children);
  },
  "joyn-sub": _ref27 => {
    let {
      children
    } = _ref27;
    return /*#__PURE__*/React.createElement("sub", null, children);
  },
  "joyn-sup": _ref28 => {
    let {
      children
    } = _ref28;
    return /*#__PURE__*/React.createElement("sup", null, children);
  },
  "joyn-date": _ref29 => {
    let {
      children
    } = _ref29;
    return /*#__PURE__*/React.createElement(Badge, {
      color: "translucentPrimary",
      fontSize: fontSize,
      s: {
        px: 3
      }
    }, moment(children).format("MMM D YYYY"));
  },
  "joyn-span": _ref30 => {
    let {
      children,
      style
    } = _ref30;
    return /*#__PURE__*/React.createElement("span", {
      style: style
    }, children);
  },
  "joyn-alert": _ref31 => {
    let {
      children,
      severity
    } = _ref31;
    return /*#__PURE__*/React.createElement(Alert, {
      severity: severity,
      s: {
        mb: 3
      }
    }, children);
  },
  "joyn-macro": _ref32 => {
    let {
      node,
      children,
      ...props
    } = _ref32;
    return /*#__PURE__*/React.createElement("span", props, children);
  },
  "joyn-expand": _ref33 => {
    let {
      children,
      title
    } = _ref33;
    return /*#__PURE__*/React.createElement(Collapsable, {
      maxHeight: 0,
      type: CollapsableTypeEnum.BUTTON,
      s: {
        mb: 3
      },
      collapsedText: title,
      expandedText: title
    }, children);
  },
  "joyn-grid": _ref34 => {
    let {
      children
    } = _ref34;
    return /*#__PURE__*/React.createElement(Box, {
      direction: "row",
      gap: 3,
      fullWidth: true
    }, children);
  },
  "joyn-grid-cell": _ref35 => {
    let {
      children
    } = _ref35;
    return /*#__PURE__*/React.createElement("div", {
      style: {
        flexGrow: 1
      }
    }, children);
  },
  "joyn-code": _ref36 => {
    let {
      children
    } = _ref36;
    const theme = useTheme();
    const isDark = theme.name.toLowerCase().includes("dark");
    return /*#__PURE__*/React.createElement(FormattedCode, {
      style: isDark ? stackoverflowDark : stackoverflowLight
    }, children);
  },
  a: _ref37 => {
    let {
      href = "",
      children
    } = _ref37;
    return /*#__PURE__*/React.createElement(Link, {
      href: href,
      color: linkColor,
      underline: true
    }, children?.join("") || href);
  },
  p: _ref38 => {
    let {
      node,
      ...props
    } = _ref38;
    return /*#__PURE__*/React.createElement(Paragraph, props);
  },
  ul: _ref39 => {
    let {
      node,
      ...props
    } = _ref39;
    return /*#__PURE__*/React.createElement(UnorderedList, props);
  },
  ol: _ref40 => {
    let {
      node,
      ...props
    } = _ref40;
    return /*#__PURE__*/React.createElement(OrderedList, props);
  },
  li: _ref41 => {
    let {
      node,
      ...props
    } = _ref41;
    return /*#__PURE__*/React.createElement(ListItem, props);
  },
  h1: _ref42 => {
    let {
      node,
      ...props
    } = _ref42;
    return /*#__PURE__*/React.createElement(H1, props);
  },
  h2: _ref43 => {
    let {
      node,
      ...props
    } = _ref43;
    return /*#__PURE__*/React.createElement(H2, props);
  },
  h3: _ref44 => {
    let {
      node,
      ...props
    } = _ref44;
    return /*#__PURE__*/React.createElement(H3, props);
  },
  h4: _ref45 => {
    let {
      node,
      ...props
    } = _ref45;
    return /*#__PURE__*/React.createElement(H4, props);
  },
  h5: _ref46 => {
    let {
      node,
      ...props
    } = _ref46;
    return /*#__PURE__*/React.createElement(H5, props);
  },
  h6: _ref47 => {
    let {
      node,
      ...props
    } = _ref47;
    return /*#__PURE__*/React.createElement(H6, props);
  },
  blockquote: _ref48 => {
    let {
      node,
      ...props
    } = _ref48;
    return /*#__PURE__*/React.createElement(Quote, props);
  },
  code: props => {
    const {
      children,
      className,
      node,
      inline,
      ...rest
    } = props;
    const theme = useTheme();
    const isDark = theme.name.toLowerCase().includes("dark");
    const match = /language-(\w+)/.exec(className || "");
    return inline ? /*#__PURE__*/React.createElement(InlineCode, _extends({}, rest, {
      className: className
    }), children) : /*#__PURE__*/React.createElement(FormattedCode, _extends({}, rest, {
      // children={String(children).replace(/\n$/, "")}
      language: match?.[0],
      style: isDark ? stackoverflowDark : stackoverflowLight
    }));
  },
  span: _ref49 => {
    let {
      node,
      ...props
    } = _ref49;
    return /*#__PURE__*/React.createElement(Span, props);
  },
  table: _ref50 => {
    let {
      node,
      ...props
    } = _ref50;
    return /*#__PURE__*/React.createElement(TableWrapper, null, /*#__PURE__*/React.createElement(Table, props));
  },
  th: _ref51 => {
    let {
      node,
      ...props
    } = _ref51;
    return /*#__PURE__*/React.createElement(Th, _extends({}, props, {
      style: !props.children ? {
        padding: 0
      } : undefined
    }));
  },
  tr: _ref52 => {
    let {
      node,
      ...props
    } = _ref52;
    return /*#__PURE__*/React.createElement(Tr, props);
  },
  td: _ref53 => {
    let {
      node,
      ...props
    } = _ref53;
    return /*#__PURE__*/React.createElement(Td, props);
  },
  ins: _ref54 => {
    let {
      node,
      ...props
    } = _ref54;
    return /*#__PURE__*/React.createElement(Ins, props);
  },
  del: _ref55 => {
    let {
      node,
      ...props
    } = _ref55;
    return /*#__PURE__*/React.createElement(Del, props);
  }
});
const Markdown = _ref56 => {
  let {
    text,
    placeholder,
    linkColor,
    components,
    ...rest
  } = _ref56;
  if (!text) {
    if (placeholder) {
      return /*#__PURE__*/React.createElement(Text, _extends({}, rest, {
        weight: "regular",
        color: "textSecondary"
      }), placeholder);
    }
    return null;
  }
  return /*#__PURE__*/React.createElement(Text, rest, /*#__PURE__*/React.createElement(ReactMarkdown, {
    components: {
      ...defaultComponents(linkColor || "secondary", rest.fontSize),
      ...components
    },
    remarkPlugins: [[remarkGfm, {
      singleTilde: true
    }]],
    rehypePlugins: [rehypeRaw]
  }, text));
};
export default Markdown;